import { AfterViewInit, Component, inject, OnInit, ViewChild } from '@angular/core';
import { AppInformationService, AuthService, GenesisUnitOfWork, PartPurchaseRepository, ReportService, UtilityService } from 'app/core/core.module';
import { Part, PartLink, PartOption, PartPartType, PartType } from 'app/core/model/entity-model';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { PartTypesSelectDialogComponent, SharedModule } from '../../shared/shared.module';
import { BulkEditDialogComponent } from '../bulk-edit-dialog/bulk-edit-dialog.component';
import { PartLookupDialogComponent } from '../part-lookup-dialog/part-lookup-dialog.component';
import { PartPurchaseOrderDialogComponent } from '../part-po-dialog/part-po-dialog.component';
import { Router } from '@angular/router';

@Component({
    selector: 'app-part-search',
    templateUrl: './part-search.component.html',
    styleUrls: ['./part-search.component.scss'],
    standalone: false,
})
export class PartSearchComponent implements OnInit {
    result: Subject<any> = new Subject<any>();
    parts: any;
    orders: any[];
    filter: any;
    isToastVisible = false;
    processing = false;
    showAsDialog = false;
    selectedParts = [];
    selectMode = 'multiple';
    partTypeIds: any;
    partOptions: any;
    mode: any;
    columnLimitMode = false;
    showMfgInfoMode = false;
    partDetailMode = false;
    canSeeCost = false;
    canSeePricing = false;
    orderLinePartsOnNew: any;
    showSideBar = false;
    salesOrderMode = false;
    purchaseOrderMode = false;
    projectionsMode = false;
    vendorId: number;
    showAliases = true;
    storageKey: string = 'part-search-grid';
    invalidId: number;
    excludePartIds: number[];
    readOnly = true;

    costProgressVisible = false;
    costProgressCurrent = 0;
    costProgressTotal = 0;
    costProgressPartNumber = '';

    get costProgressPercent(): number {
        return this.costProgressTotal > 0 ? Math.round((this.costProgressCurrent / this.costProgressTotal) * 100) : 0;
    }

    @ViewChild('partsGrid') partsGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    router = inject(Router);
    uow = inject(GenesisUnitOfWork);
    modalService = inject(BsModalService);
    constructor(
        public auth: AuthService,
        private appInfo: AppInformationService,
        private utility: UtilityService,
        private partPurchaseRepository: PartPurchaseRepository,
    ) {}

    async ngOnInit() {
        this.showMfgInfoMode = this.columnLimitMode && this.mode !== 'Options' && this.mode !== 'Replacements';
        const partPartTypes = await this.uow.partPartTypes.all();
        this.setPermissions();
        await this.getData();
        await this.getOrderLineDetailParts();
        await this.getParentOnOrderQuantities();
        if (!this.salesOrderMode && !this.purchaseOrderMode) await this.getInventoryAdjustments();
    }

    onGridInitialized(e) {
        if (this.showAsDialog) {
            // Use setTimeout to ensure state has been restored from storage
            setTimeout(() => {
                const currentState = e.component.state();
                if (currentState?.selectedRowKeys?.length > 0) {
                    currentState.selectedRowKeys = [];
                    e.component.state(currentState);
                }
            });
        }
    }

    setPermissions() {
        this.canSeeCost = (this.auth.hasRole('Parts:CanSeeCost') || this.auth.hasRole('Parts:Admin')) && !this.showAsDialog && !this.columnLimitMode && !this.partDetailMode;
        this.canSeePricing = (this.auth.hasRole('Parts:CanSeePricing') || this.auth.hasRole('Parts:Admin')) && !this.showAsDialog && !this.columnLimitMode && !this.partDetailMode;
        this.readOnly = !this.auth.hasRole('Parts:Admin');
    }
    async getData() {
        if (!!this.partOptions && this.partOptions.length > 0) {
            // get the data specific to options - used on Build Detail for Add Options
            console.log('buildDetailMode');
            this.showAliases = false;
            this.parts = this.partOptions;
        } else if (!!this.partTypeIds && this.salesOrderMode) {
            // Sales Order Detail
            // used when returning the data of only parts that match a certain PartType
            console.log('salesOrderMode');
            const options: any = {};
            if (!!this.partTypeIds && this.partTypeIds.length > 0) options.partTypeIds = this.partTypeIds;

            // options.expand = 'purchaseOrderLines.purchaseOrder';
            const filteredParts = (await this.uow.getParts(options)) as Part[];
            this.parts = filteredParts;
        } else if (!!this.partTypeIds && this.purchaseOrderMode) {
            // purchaseOrderMode - Purchase Order Detail
            console.log('purchaseOrderMode');
            const options: any = {};
            options.partTypeIds = this.partTypeIds;
            // const filteredParts = (await this.uow.getParts(options)) as Part[];
            const filteredParts = (await this.uow.getPartsForPurchaseOrder(this.vendorId)) as Part[];
            this.parts = filteredParts;
            await this.getPurchaseOrderInfosForParts();
        } else if (!!this.partTypeIds && this.projectionsMode) {
            console.log('projectionsMode');
            const options: any = {};
            if (!!this.partTypeIds && this.partTypeIds.length > 0) options.partTypeIds = this.partTypeIds;

            // options.expand = 'purchaseOrderLines.purchaseOrder';
            const filteredParts = (await this.uow.getParts(options)) as Part[];
            this.parts = filteredParts;
        } else if (!!this.partTypeIds) {
            // used when returning the data of only parts that match a certain PartType
            console.log('buildAssemblyMode');
            const options: any = {};
            if (!!this.partTypeIds && this.partTypeIds.length > 0) options.partTypeIds = this.partTypeIds;
            options.isAssembly = true;

            // options.expand = 'purchaseOrderLines.purchaseOrder';
            const filteredParts = (await this.uow.getParts(options)) as Part[];
            this.parts = filteredParts;
        } else {
            console.log('partSearchMode | buildDetailBulkAddMode');
            // used when returning full list of parts
            // const statuses = this.uow.lookups.purchaseOrderStatuses;
            // const purchaseOrders = await this.uow.purchaseOrders.all();
            const options: any = {};
            if (this.excludePartIds) options.excludePartIds = this.excludePartIds;
            // options.expand = 'partAttachments';

            const parts = (await this.uow.getPartsForSearch(options)) as Part[];
            this.parts = parts;
            this.parts.forEach((x) => {
                x.costMismatch = x.calculatedCost !== x.cost;
                const configs = x.partConfigurations.filter((pc) => !pc.deleted && pc.processed);
                if (configs.length > 0) {
                    const orderedConfigs = _.orderBy(configs, ['processed'], ['desc']);
                    x.lastProcessed = orderedConfigs[0]?.processed;
                }
            });
            await this.getPurchaseOrderInfosForParts();
            await this.getPartAttachments();
        }
        if (this.showAliases) this.setAliasHTML();
    }

    async getPartAttachments() {
        const attachments = await this.uow.partAttachments.whereActive();
        this.parts.forEach((part) => {
            const partAttachments = attachments.filter((alias) => alias.partId === part.id);
            if (!!partAttachments) {
                const list = [];
                partAttachments.forEach((x) => {
                    list.push({ id: x.id, name: x.name, filename: x.filename });
                });
                part.attachmentsList = list;
            }
        });
    }

    async setAliasHTML() {
        const aliases = (await this.uow.getActivePartAliases({ select: 'partId, partNumber, vendor.companyName', orderBy: 'vendor.companyName, partNumber' })) as any[];
        this.parts.forEach((part) => {
            const partAliases = aliases.filter((alias) => alias.partId === part.id);
            part.aliasHTML = partAliases.map((alias) => `${alias.vendor_CompanyName} -- ${alias.partNumber ?? ''}`).join('<br/>');
        });
    }

    async getPurchaseOrderInfosForParts() {
        const purchaseOrderInfos = await this.uow.getPurchaseOrderInfosForParts();
        this.parts.forEach((part) => {
            const info = _.find(purchaseOrderInfos, { partId: part.id }) as any;
            part._onOrder = !!info ? info.onOrder : 0;
            part._onBackOrder = !!info ? info.onBackOrder : 0;
        });
    }

    async getOrderLineDetailParts() {
        const orderLinePartsNew = await this.uow.getOrderLineDetailPartsOnNew();
        this.orderLinePartsOnNew = orderLinePartsNew;
        this.parts.forEach((part) => {
            const orderLinePartInfo = _.find(orderLinePartsNew, { partId: part.id }) as any;
            part.allocated = !!orderLinePartInfo ? orderLinePartInfo.partCountOnNewStatus : 0;
            part.projectedInventory = part.inStock - part.allocated;
        });
    }

    async getParentOnOrderQuantities() {
        const partQuantities = await this.uow.getParentOnOrderQuantities();
        this.parts.forEach((part) => {
            const parentLine = _.find(partQuantities, { id: part.id }) as any;
            part.onOrderParent = !!parentLine ? parentLine.totalOnOrder : 0;
        });
    }

    async getInventoryAdjustments() {
        const adjustments = await this.utility.getInventoryAdjustments();
        this.parts.forEach((part) => {
            const adjustment = _.find(adjustments, { Key: part.id }) as any;
            if (!!adjustment) {
                const partAdjustment = adjustment.PartAdjustment;
                const adjustmentDate = moment(partAdjustment.Date).format('MM/DD/YYYY');
                part.lastAdjustment = `${adjustmentDate} - ${partAdjustment.CreatedBy} - Reason: ${partAdjustment.Reason}`;
            }
        });
    }

    async refreshCalcCosts() {
        this.costProgressCurrent = 0;
        this.costProgressTotal = 0;
        this.costProgressPartNumber = '';
        this.costProgressVisible = true;

        try {
            const { jobId } = await this.utility.startCalculatedCostJob();
            await this.pollCostProgress(jobId);
        } catch (e: any) {
            alert(`Error refreshing calculated costs: ${e?.message ?? e}`, 'Error');
        } finally {
            this.costProgressVisible = false;
        }
        this.appInfo.pleaseWaitShow();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    private pollCostProgress(jobId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const interval = setInterval(async () => {
                try {
                    const p = await this.utility.getCalculatedCostProgress(jobId);
                    this.costProgressCurrent = p.processed ?? 0;
                    this.costProgressTotal = p.total ?? 0;
                    this.costProgressPartNumber = p.currentPartNumber ?? '';

                    if (p.status === 'completed') {
                        clearInterval(interval);
                        resolve();
                    } else if (p.status === 'error' || p.status === 'notfound') {
                        clearInterval(interval);
                        reject(new Error(p.errorMessage || 'Job not found'));
                    }
                } catch (e) {
                    clearInterval(interval);
                    reject(e);
                }
            }, 500);
        });
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    search() {}
    clear() {}

    // this method is used to clear the selection when the user searches for something
    onOptionChanged(e) {
        if (e.fullName === 'searchPanel.text') {
            e.component.__searchChanged = true;
        }
        const filterCheck = _.includes(e.fullName, 'filterValue');
        if (filterCheck) {
            this.partsGrid.instance.clearSelection();
        }
    }

    // this method is used to clear the selection when the user searches for something
    onContentReady(e) {
        if (e.component.__searchChanged) {
            //__searchChanged is for the search box
            e.component.__searchChanged = false;
            this.partsGrid.instance.clearSelection();
        }
    }

    async save() {
        await this.partsGrid.instance.saveEditData();
        this.isToastVisible = true;
        await this.uow.commit();
    }

    async cancelEdits() {
        this.partsGrid.instance.cancelEditData();
        this.uow.rollback();
        await this.partsGrid.instance.refresh();
    }

    onToolbarPreparing(e) {
        const toolbarItems = e.toolbarOptions.items;
        const saveButton = toolbarItems.filter((item) => item.name === 'saveButton')[0];
        if (saveButton) toolbarItems.splice(toolbarItems.indexOf(saveButton), 1);
        const revertButton = toolbarItems.filter((item) => item.name === 'revertButton')[0];
        if (revertButton) toolbarItems.splice(toolbarItems.indexOf(revertButton), 1);

        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }

    async addPartPurchase(selectedPart: any) {
        if (await this.partPurchaseRepository.addPartPurchase(selectedPart)) {
            await this.getData();
            await this.getOrderLineDetailParts();
        }
    }

    rowDoubleClick(e) {
        // console.log(e);
        if (!e.data || !e.data.id) return;
        if (e.column.name === 'id' || e.column.name === 'inStock' || e.column.name === 'allocated' || e.column.name === 'projectedInventory') this.open(e.data.id);
    }

    rowPrepare(e) {
        if (e.rowType === 'data') {
            // if (!!e.data.cost && !!e.data.calculatedCost && e.data.calculatedCost !== 0) {
            if (e.data.cost === 0 && e.data.calculatedCost === 0) e.rowElement.className = e.rowElement.className + ' part-search-yellow-grid';
            // if (e.data.calculatedCost > e.data.price * 2) e.rowElement.className = e.rowElement.className + ' part-search-red-grid';
            // }
            if (!!e.data.fontColor) {
                e.rowElement.style.color = `${e.data.fontColor}`;
                e.rowElement.style.fontWeight = 'bold';
            }
        }
    }

    setFontColor() {
        const selectedRows = this.partsGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'color',
                dialogHeader: 'Set Font Color',
                dialogPrompt: 'Select Font Color',
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.result) {
                selectedRows.forEach((row) => {
                    row.fontColor = result.value;
                });
                await this.uow.commit();
                await this.getData();
            }
            modalRef.hide();
        });
    }

    async open(partId: number) {
        this.router.navigate(['/parts/' + (partId ? partId : 'new')]);
    }

    async delete(selection: any) {
        let partNames = '';
        selection.forEach((part) => {
            partNames += part.partNumber + '\r\n';
        });
        const result = await (confirm(
            `Are you sure you want to DELETE ${selection.length === 1 ? 'this part?\r\n\n' + partNames : 'these ' + '(' + selection.length + ')' + ' parts?\r\n\n' + partNames}`,
            `${this.appInfo.settings.company}`,
        ) as Promise<boolean>);

        // check for open purchase orders
        const partIds = selection.map((x) => {
            return x.id;
        });

        this.appInfo.pleaseWaitShow();
        const openOrders = (await this.uow.getOpenPurchaseOrdersByPartIds(partIds)) as any;
        // console.log(openOrders);
        if (!!openOrders && openOrders.length > 0) {
            let message = [];
            let poMessage = [];
            openOrders.forEach((openOrder) => {
                openOrder.pOs.forEach((po) => {
                    poMessage.push(`PO Number: ${po}`);
                });
                message.push(`${openOrder.partNumber}\r\n` + poMessage.join(`\r\n`) + '\r\n');
            });
            this.appInfo.pleaseWaitHide();
            await alert(`The following parts cannot be deleted because they exist on an open Purchase Order:\r\n${message.join(`\r\n`)}`, `${this.appInfo.settings.company}`);
            return;
        }

        const partLinks = await this.uow.getActivePartLinksBySubPartIds(partIds);
        // console.log(partLinks);
        let partLinkList: string[] = [];
        if (partLinks && partLinks.length > 0) {
            let partNames = '';
            this.appInfo.pleaseWaitShow();

            for (let index = 0; index < selection.length; index++) {
                const part = selection[index];
                for (let index = 0; index < partLinks.length; index++) {
                    const partLink = partLinks[index];
                    if (partLink.subPartId === part.id) {
                        const partLinkPart = (await this.uow.parts.byId(partLink.partId)) as Part;
                        if (!!partLinkPart) {
                            // console.log(partLinkPart);
                            partLinkList.push(partLinkPart.partNumber);
                        }
                    }
                }
                partLinks.forEach((partLink) => {});
                if (partLinkList.length > 0) {
                    // console.log('partLinkList', partLinkList);
                    partNames += `${part.partNumber} is a link for part${partLinkList.length > 1 ? 's' : ''}:\n&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${partLinkList.join(
                        '\n&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;',
                    )}\r\n`;
                }
            }
            this.appInfo.pleaseWaitHide();

            if (partLinkList.length > 0) {
                const results = await confirm(
                    `The following parts are part links:\r\n\n${partNames}\nDeleting ${
                        partIds.length > 1 ? 'these parts' : 'this part'
                    }  will delete their part links as well. Do you want to continue?`,
                    `${this.appInfo.settings.company}`,
                );
                if (!results) return;
            }

            partLinks.forEach((partLink) => {
                partLink.deleted = moment(new Date()).toDate();
                partLink.deletedBy = this.auth.currentUser.userName;
            });
        }

        if (!!result) {
            selection.forEach((row) => {
                row.deleted = new Date();
            });
            this.appInfo.pleaseWaitShow();
            await this.uow.commit();
            await this.getData();
            this.appInfo.pleaseWaitHide();
        }
    }

    async duplicate() {
        if (this.partsGrid.instance.getSelectedRowsData().length === 0 || this.processing) return;
        const selectedPart = this.partsGrid.instance.getSelectedRowsData()[0];
        // console.log(selectedPart);

        const result = await (confirm(`Are you sure you want to DUPLICATE part: ${selectedPart.partNumber}?`, `${this.appInfo.settings.company}`) as Promise<boolean>);
        if (!result) return;
        this.processing = true;
        this.appInfo.pleaseWaitShow();
        this.uow.clearCached('Part');
        this.uow.clearCached('PartLink');

        const originalPart = (await this.uow.parts.byId(selectedPart.id, 'partLinks, partPartTypes')) as Part;
        const originalPartOptions = (await this.uow.getPartOptionsByPartId(selectedPart.id)) as PartOption[];
        // console.log('orig', originalPart);
        const newPart = this.uow.parts.createEntity() as Part;
        newPart.reportGuid = crypto.randomUUID();
        newPart.partNumber = originalPart.partNumber + ' - Copy';
        newPart.inStock = 0;
        newPart.description = originalPart.description;
        newPart.reOrderMinimum = originalPart.reOrderMinimum;
        newPart.reOrderQuantity = originalPart.reOrderQuantity;
        newPart.mfgPartNumber = originalPart.mfgPartNumber;
        newPart.mfgDescription = originalPart.mfgDescription;
        newPart.cost = originalPart.cost;
        newPart.price = originalPart.price;
        newPart.preferredVendor = originalPart.preferredVendor;
        newPart.unitOfMeasure = originalPart.unitOfMeasure;
        newPart.category = originalPart.category;
        newPart.created = new Date();

        originalPart.partPartTypes.forEach((partPartType) => {
            const newPartPartType = this.uow.partPartTypes.createEntity() as PartPartType;
            newPartPartType.partTypeId = partPartType.partTypeId;
            newPartPartType.part = newPart;
            newPart.partPartTypes.push(newPartPartType);
        });

        originalPartOptions.forEach((option) => {
            if (!option.deleted) {
                const newOption = this.uow.partOptions.createEntity() as PartOption;
                newOption.partId = newPart.id;
                newOption.optionPartId = option.optionPartId;
            }
        });

        originalPart.partLinks.forEach((link) => {
            if (!link.deleted) {
                const newLink = this.uow.partLinks.createEntity() as PartLink;

                newLink.subPartId = link.subPartId;
                newLink.quantity = link.quantity;
                newLink.createdBy = this.auth.currentUser.userName;
                newLink.created = new Date();
                newPart.partLinks.push(newLink);
            }
        });

        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
        this.processing = false;
    }

    async select(closeOnSelect: boolean) {
        if (this.partsGrid.instance.getSelectedRowsData().length === 0) return;
        const selectedParts = this.partsGrid.instance.getSelectedRowsData();
        let partsSelected = 0;

        if (
            selectedParts.filter((part) => {
                return part.id === this.invalidId;
            }).length > 0
        ) {
            alert(`Part cannot be added to itself`, `${this.appInfo.settings.company}`);
            return;
        }

        selectedParts.forEach((part) => {
            // Allowing duplicates
            this.selectedParts.push(part);
            partsSelected++;
        });
        if (partsSelected > 0) this.appInfo.notifyToast(`${partsSelected} parts have been added`, 'success', 2000);
        if (closeOnSelect) this.closeDialog();
    }

    removeFromSelected(info: any) {
        const index = this.selectedParts.indexOf(info);
        if (index >= 0) this.selectedParts.splice(index, 1);
        if (this.selectedParts.length === 0) {
        }
    }

    cancel() {
        if (this.selectedParts.length !== 0) {
            this.appInfo.notifyToast('Removing added parts', 'info', 1000);
        }
        this.result.next({ success: false });
    }

    closeDialog() {
        if (this.showAsDialog) {
            const selectedPartIds = this.selectedParts.map((x) => {
                return x.id;
            });
            this.result.next({ success: this.selectedParts.length > 0 ? true : false, partIds: selectedPartIds.length > 0 ? selectedPartIds : null });
        }
    }

    async partOptionBulkEdit() {
        const modalRef = this.modalService.show(PartSearchComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                showSideBar: true,
            },
        });

        const selectedParts = this.partsGrid.instance.getSelectedRowsData();

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                for (let partIndex = 0; partIndex < selectedParts.length; partIndex++) {
                    const element = selectedParts[partIndex];

                    for (let index = 0; index < result.partIds.length; index++) {
                        const partOption = this.uow.partOptions.createEntity() as PartOption;
                        partOption.part = element;
                        partOption.optionPartId = result.partIds[index];
                    }
                }
                await this.save();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    partTypeBulkEdit() {
        const modalRef = this.modalService.show(PartTypesSelectDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {},
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.add) {
                // get the list of part types selected
                const partTypes = (await this.uow.partTypes.byIds(result.partTypeIds)) as PartType[];

                // for each selected parts, check if part types need to be removed or added
                const parts = this.partsGrid.instance.getSelectedRowsData();
                parts.forEach((part: Part) => {
                    partTypes.forEach((partType: PartType) => {
                        const foundIndex = part.partPartTypes.findIndex((partPartType) => partPartType.partTypeId === partType.id);
                        if (foundIndex === -1) {
                            const partPartType: any = this.uow.partPartTypes.createEntity();
                            partPartType.partTypeId = result.partTypeId;
                            partType.partPartTypes.push(partPartType);
                            part.partPartTypes.push(partPartType);
                        }
                    });
                });
                await this.save();
            }
            if (result.delete) {
                // get the list of part types selected
                const partTypes = (await this.uow.partTypes.byIds(result.partTypeIds)) as PartType[];

                // for each selected parts, check if part types need to be removed or added
                const parts = this.partsGrid.instance.getSelectedRowsData();
                parts.forEach((part: Part) => {
                    let idx = 0;
                    while (idx < part.partPartTypes.length) {
                        const currentPartPartType = part.partPartTypes[idx];
                        const foundIndex = partTypes.findIndex((partType: PartType) => partType.id === currentPartPartType.partTypeId);
                        if (foundIndex !== -1) {
                            const partType = currentPartPartType.partType;
                            part.partPartTypes.splice(idx, 1);
                            const spliceIndex = partType.partPartTypes.findIndex((partPartType) => partPartType.id === currentPartPartType.id);
                            partType.partPartTypes.splice(spliceIndex, 1);
                            currentPartPartType.entityAspect.setDeleted();
                        } else {
                            idx++;
                        }
                    }
                });
                await this.save();
            }
            modalRef.hide();
        });
    }

    lookup(partId) {
        const modalRef = this.modalService.show(PartLookupDialogComponent, {
            class: 'modal-mdw',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                partId: partId,
            },
        });
    }

    async purchaseOrderDialog(partId: number) {
        const modalRef = this.modalService.show(PartPurchaseOrderDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                partId: partId,
            },
        });

        modalRef.content.result.subscribe((result) => {
            if (result.success && !!result.value) {
                this.openPurchaseOrder(result.value);
            }
            modalRef.hide();
        });
    }

    async openPurchaseOrder(id: number) {
        this.router.navigate(['/purchase-orders/' + id]);
    }

    async bulkEdit(selectedParts: any) {
        if (!selectedParts) return;
        const partIds = selectedParts.map((x) => {
            return x.id;
        });
        const modalRef = this.modalService.show(BulkEditDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                bulkInfo: new BulkInfo(),
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success && result.value) {
                const bulkInfo = result.value as BulkInfo;
                if (!bulkInfo.vendor && !bulkInfo.fontColor && !bulkInfo.category && !bulkInfo.isAssembly) return;
                this.appInfo.pleaseWaitShow();
                selectedParts.forEach((part) => {
                    if (bulkInfo.vendor) {
                        part.preferredVendor = bulkInfo.vendor.companyName;
                        part.vendorId = bulkInfo.vendor.id;
                    }

                    if (bulkInfo.category) part.category = bulkInfo.category;
                    if (bulkInfo.fontColor) part.fontColor = bulkInfo.fontColor;
                    if (bulkInfo.isAssembly) part.isAssembly = bulkInfo.isAssembly === 'True' ? true : false;
                });
                await this.uow.commit();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    openAttachment(attachment: any) {
        // console.log('hello world', attachment);
        window.open(attachment.filename, '_blank');
    }
}
class BulkInfo {
    vendor: any;
    category: any;
    fontColor: any;
    isAssembly: any;
}
